import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Mail, Calendar, HardDrive, CheckCircle2, XCircle, RefreshCw, Link, Unlink, FileSignature, Ticket } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { SiGoogle, SiPaypal, SiStripe } from "react-icons/si";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";
import { PaymentPortalDialog } from "@/components/payment-portal-dialog";

interface IntegrationStatus {
  connected: boolean;
  email: string | null;
  isWorkspaceLevel?: boolean;
}

interface UserIntegration {
  userId: string;
  name: string;
  email: string;
  role: string;
  gmail: IntegrationStatus;
  calendar: IntegrationStatus;
}

interface SmsIntegrationStatus {
  connected: boolean;
  provider: string | null;
  phoneNumber: string | null;
}

interface DocusignIntegrationStatus {
  connected: boolean;
  email: string | null;
}

interface EventbriteIntegrationStatus {
  connected: boolean;
  email: string | null;
  accountName: string | null;
  platformEnabled: boolean;
}

interface ByoaServiceStatus {
  isConfigured: boolean;
}

interface TwilioDialerStatus {
  isConfigured: boolean;
  hasApiKeySid: boolean;
  hasApiKeySecret: boolean;
  hasVoiceAppSid: boolean;
  accountSid?: string | null;
}

interface IntegrationsData {
  users: UserIntegration[];
  workspaceIntegrations: {
    calendar: IntegrationStatus;
    drive: IntegrationStatus;
    stripe: IntegrationStatus;
    paypal: IntegrationStatus;
    sms: SmsIntegrationStatus;
    docusign: DocusignIntegrationStatus;
    eventbrite?: EventbriteIntegrationStatus;
    // Task #57 — BYOA
    elevenLabs?: ByoaServiceStatus;
    docusignHmac?: ByoaServiceStatus;
    twilioDialer?: TwilioDialerStatus;
  };
  emailFallbackEnabled?: boolean;
  fallbackEmailUserId?: string | null;
  fallbackEmailUserEmail?: string | null;
}

interface IntegrationsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

// BYOA Eventbrite — workspace pastes their own client_id/client_secret
// before /oauth/start can mint an authorize URL.
function EventbriteConnectFlow({ onConnected }: { onConnected: () => void }) {
  const { toast } = useToast();
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedOnce, setSavedOnce] = useState(false);
  const [starting, setStarting] = useState(false);

  const saveCreds = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      toast({ title: "Both fields required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiRequest("PUT", "/api/eventbrite/connection/credentials", {
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
      });
      setSavedOnce(true);
      toast({ title: "Eventbrite app credentials saved" });
    } catch (e: any) {
      toast({ title: "Failed to save credentials", description: e?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const startOauth = async () => {
    setStarting(true);
    try {
      const res = await apiRequest("GET", "/api/eventbrite/oauth/start");
      const json = await res.json();
      if (json?.url) window.location.href = json.url;
    } catch (e: any) {
      toast({
        title: "Failed to start OAuth",
        description: e?.message || "Save your Eventbrite client_id and client_secret first.",
        variant: "destructive",
      });
    } finally {
      setStarting(false);
      onConnected();
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Link Eventbrite so ticketed shows settle automatically when EB pays out, and ticket buyers flow into your marketing list.
      </p>
      <div className="rounded-md border bg-muted/30 p-3 space-y-3">
        <div className="text-xs text-muted-foreground">
          1. Create an Eventbrite API app at{" "}
          <a
            href="https://www.eventbrite.com/account-settings/apps"
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            eventbrite.com/account-settings/apps
          </a>{" "}
          and paste its credentials below. 2. Save. 3. Click Connect.
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <Label htmlFor="eb-client-id" className="text-xs">Client ID</Label>
            <Input
              id="eb-client-id"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Your Eventbrite app's API key / Client ID"
              data-testid="input-eventbrite-client-id"
            />
          </div>
          <div>
            <Label htmlFor="eb-client-secret" className="text-xs">Client Secret</Label>
            <Input
              id="eb-client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Your Eventbrite app's Client Secret"
              data-testid="input-eventbrite-client-secret"
            />
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={saveCreds}
          disabled={saving}
          data-testid="button-save-eventbrite-credentials"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Save Eventbrite App Credentials
        </Button>
      </div>
      <Button
        variant="default"
        size="sm"
        onClick={startOauth}
        disabled={starting || (!savedOnce && !clientId.trim())}
        data-testid="button-connect-eventbrite"
      >
        <Link className="w-4 h-4 mr-2" />
        Connect Eventbrite
      </Button>
      {!savedOnce && (
        <p className="text-xs text-muted-foreground">
          Save credentials first — Connect won't work until they're stored.
        </p>
      )}
    </div>
  );
}

// ─── BYOA: DocuSign Connect HMAC ─────────────────────────────────────────────
function DocusignHmacCard({
  status,
  workspaceSlug,
  onSaved,
}: {
  status?: ByoaServiceStatus;
  workspaceSlug: string | null;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [hmacKey, setHmacKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const save = async () => {
    if (!hmacKey.trim()) {
      toast({ title: "HMAC key required", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await apiRequest("PATCH", `/api/w/${workspaceSlug}/integrations/docusign-hmac`, { hmacKey: hmacKey.trim() });
      toast({ title: "DocuSign Connect HMAC key saved" });
      setOpen(false);
      setHmacKey("");
      onSaved();
    } catch (e: any) {
      toast({ title: "Failed to save", description: e?.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const disconnect = async () => {
    setDisconnecting(true);
    try {
      await apiRequest("DELETE", `/api/w/${workspaceSlug}/integrations/docusign-hmac`);
      toast({ title: "DocuSign HMAC key removed" });
      onSaved();
    } catch (e: any) {
      toast({ title: "Failed to disconnect", description: e?.message, variant: "destructive" });
    } finally {
      setDisconnecting(false);
    }
  };

  return (
    <div className="space-y-3">
      {status?.isConfigured ? (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            DocuSign Connect HMAC key is configured. Inbound e-signature webhook events will be verified against your workspace key.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="button-edit-docusign-hmac">
              Update Key
            </Button>
            <Button variant="ghost" size="sm" onClick={disconnect} disabled={disconnecting} data-testid="button-disconnect-docusign-hmac">
              {disconnecting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Unlink className="w-4 h-4 mr-2" />}
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Add your DocuSign Connect HMAC key so incoming e-signature webhook events are verified with your workspace's secret. Configure it in{" "}
            <a href="https://admindemo.docusign.com/connect" target="_blank" rel="noreferrer" className="underline">
              DocuSign Admin → Connect
            </a>.
          </p>
          <Button variant="default" size="sm" onClick={() => setOpen(true)} data-testid="button-configure-docusign-hmac">
            <FileSignature className="w-4 h-4 mr-2" />
            Add HMAC Key
          </Button>
        </div>
      )}

      {open && (
        <div className="rounded-md border bg-muted/30 p-4 space-y-3 mt-2">
          <p className="text-xs text-muted-foreground">
            Find this under DocuSign Admin → Connect → your Connect configuration → HMAC Keys.
          </p>
          <div className="space-y-2">
            <Label htmlFor="ds-hmac-key" className="text-xs">Connect HMAC Key</Label>
            <Input
              id="ds-hmac-key"
              type="password"
              value={hmacKey}
              onChange={e => setHmacKey(e.target.value)}
              placeholder="Paste your DocuSign Connect HMAC key"
              data-testid="input-docusign-hmac-key"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} data-testid="button-save-docusign-hmac">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="button-cancel-docusign-hmac">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export function IntegrationsDialog({ open, onOpenChange, inPanel = false }: IntegrationsDialogProps) {
  const { toast } = useToast();
  const { workspaceSlug, buildPath } = useWorkspace();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showPaymentPortal, setShowPaymentPortal] = useState(false);
  
  const { data, isLoading, refetch, isRefetching } = useQuery<IntegrationsData>({
    queryKey: [buildPath("/api/admin/integrations/status")],
    enabled: open,
  });

  interface TalentCalendarStatus {
    talentId: string;
    talentName: string;
    talentEmail: string | null;
    calendarConnected: boolean;
    calendarEmail: string | null;
    pendingInvite: boolean;
    inviteExpiresAt: string | null;
  }
  
  const { data: talentCalendarData, refetch: refetchTalentCalendars, isLoading: talentCalendarLoading } = useQuery<{ talent: TalentCalendarStatus[] }>({
    queryKey: [buildPath("/api/talent/calendar/status")],
    enabled: open,
  });

  const sendCalendarInviteMutation = useMutation({
    mutationFn: async (talentId: string) => {
      const response = await apiRequest("POST", `/api/talent/${talentId}/calendar/invite`);
      return response.json();
    },
    onSuccess: () => {
      refetchTalentCalendars();
      toast({ title: "Invitation Sent", description: "Calendar connection invitation has been sent." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to send invitation", variant: "destructive" });
    }
  });

  const revokeCalendarInviteMutation = useMutation({
    mutationFn: async (talentId: string) => {
      await apiRequest("POST", `/api/talent/${talentId}/calendar/revoke`);
    },
    onSuccess: () => {
      refetchTalentCalendars();
      toast({ title: "Invitation Revoked", description: "The pending invitation has been revoked." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to revoke invitation", variant: "destructive" });
    }
  });

  const disconnectTalentCalendarMutation = useMutation({
    mutationFn: async (talentId: string) => {
      await apiRequest("POST", `/api/talent/${talentId}/calendar/disconnect`);
    },
    onSuccess: () => {
      refetchTalentCalendars();
      toast({ title: "Calendar Disconnected", description: "The talent's calendar has been disconnected." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect calendar", variant: "destructive" });
    }
  });
  
  const disconnectCalendarMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/w/${workspaceSlug}/calendar/disconnect`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] });
      toast({ title: "Calendar Disconnected", description: "Google Calendar has been disconnected from your workspace." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect calendar", variant: "destructive" });
    }
  });

  const connectDriveMutation = useMutation({
    mutationFn: async () => {
      const currentUrl = window.location.href;
      const response = await apiRequest("GET", buildPath(`/api/drive/auth?returnUrl=${encodeURIComponent(currentUrl)}`));
      const data = await response.json();
      return data.authUrl;
    },
    onSuccess: (authUrl: string) => {
      window.location.href = authUrl;
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to initiate Google Drive connection", variant: "destructive" });
    },
  });

  const disconnectDriveMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", buildPath("/api/drive/disconnect"));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] });
      toast({ title: "Google Drive Disconnected", description: "Google Drive has been disconnected." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect Drive", variant: "destructive" });
    }
  });

  const disconnectPayPalMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/w/${workspaceSlug}/paypal/disconnect`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] });
      toast({ title: "PayPal Disconnected", description: "PayPal has been disconnected from your workspace." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to disconnect PayPal", variant: "destructive" });
    }
  });

  const handleConnectCalendar = async () => {
    if (!workspaceSlug) return;
    setIsConnecting(true);
    try {
      const response = await fetch(`/api/w/${workspaceSlug}/calendar/auth`);
      const data = await response.json();
      if (data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        toast({ title: "Error", description: "Failed to get authorization URL", variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: "Error", description: error.message || "Failed to connect calendar", variant: "destructive" });
    } finally {
      setIsConnecting(false);
    }
  };

  const connectedCount = data?.users?.filter(u => u.gmail.connected).length || 0;
  const totalUsers = data?.users?.length || 0;

  const innerContent = (<>
        {!inPanel && (
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SiGoogle className="w-5 h-5" />
              Integrations
            </DialogTitle>
            <DialogDescription>
              Manage and monitor all connected services
            </DialogDescription>
          </DialogHeader>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
              <TabsTrigger value="users" data-testid="tab-users">User Connections</TabsTrigger>
              <TabsTrigger value="workspace" data-testid="tab-workspace">Workspace Services</TabsTrigger>
              <TabsTrigger value="talent" data-testid="tab-talent">Talent Calendars</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="flex-1 overflow-auto mt-4">
              <div className="grid gap-4 md:grid-cols-2">
                <Card data-testid="card-gmail-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-red-500" />
                      <CardTitle className="text-base">Gmail</CardTitle>
                    </div>
                    <CardDescription>Email sending and inbox tracking</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Users connected</span>
                      <Badge variant={connectedCount > 0 ? "default" : "secondary"} data-testid="badge-gmail-count">
                        {connectedCount} / {totalUsers}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>

                <Card data-testid="card-calendar-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-500" />
                      <CardTitle className="text-base">Google Calendar</CardTitle>
                    </div>
                    <CardDescription>Event booking and availability</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {data?.workspaceIntegrations.calendar.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-calendar-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-calendar-disconnected">
                          <XCircle className="w-3 h-3 mr-1" />
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    {data?.workspaceIntegrations.calendar.email && (
                      <p className="text-xs text-muted-foreground mt-2" data-testid="text-calendar-email">
                        {data.workspaceIntegrations.calendar.email}
                      </p>
                    )}
                  </CardContent>
                </Card>

                {/* Google Drive overview card — hidden from UI; queries/mutations remain intact. To re-enable, un-comment this block. */}
                {false && (
                <Card data-testid="card-drive-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-5 h-5 text-yellow-500" />
                      <CardTitle className="text-base">Google Drive</CardTitle>
                    </div>
                    <CardDescription>Read-only Google Sheets sync</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {data?.workspaceIntegrations.drive.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-drive-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-drive-disconnected">
                          <XCircle className="w-3 h-3 mr-1" />
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    {!data?.workspaceIntegrations.drive.connected && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Set up in Replit's Integrations panel
                      </p>
                    )}
                  </CardContent>
                </Card>
                )}

                <Card data-testid="card-stripe-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <SiStripe className="w-5 h-5 text-purple-600" />
                      <CardTitle className="text-base">Stripe</CardTitle>
                    </div>
                    <CardDescription>Credit card payments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {data?.workspaceIntegrations.stripe?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-stripe-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-stripe-disconnected">
                          <XCircle className="w-3 h-3 mr-1" />
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    {!data?.workspaceIntegrations.stripe?.connected && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Configure in Workspace Services
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="card-paypal-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <SiPaypal className="w-5 h-5 text-blue-600" />
                      <CardTitle className="text-base">PayPal</CardTitle>
                    </div>
                    <CardDescription>PayPal payments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {data?.workspaceIntegrations.paypal?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-paypal-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-paypal-disconnected">
                          <XCircle className="w-3 h-3 mr-1" />
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    {!data?.workspaceIntegrations.paypal?.connected && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Configure in Workspace Services
                      </p>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="card-docusign-overview">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                      <FileSignature className="w-5 h-5 text-yellow-600" />
                      <CardTitle className="text-base">DocuSign</CardTitle>
                    </div>
                    <CardDescription>E-signatures and contracts</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      {data?.workspaceIntegrations.docusign?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-docusign-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-docusign-disconnected">
                          <XCircle className="w-3 h-3 mr-1" />
                          Not Connected
                        </Badge>
                      )}
                    </div>
                    {data?.workspaceIntegrations.docusign?.email && (
                      <p className="text-xs text-muted-foreground mt-2" data-testid="text-docusign-email">
                        {data.workspaceIntegrations.docusign.email}
                      </p>
                    )}
                    {!data?.workspaceIntegrations.docusign?.connected && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Configure in Workspace Services
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="users" className="flex-1 overflow-auto mt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground" data-testid="text-users-summary">
                  {connectedCount} of {totalUsers} users have Gmail connected
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => refetch()}
                  disabled={isRefetching}
                  data-testid="button-refresh-status"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefetching ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <ScrollArea className="h-[400px]">
                <div className="space-y-2">
                  {data?.users?.map((user) => (
                    <Card key={user.userId} className="p-4" data-testid={`card-user-${user.userId}`}>
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" data-testid={`text-user-name-${user.userId}`}>{user.name}</p>
                          <p className="text-sm text-muted-foreground truncate" data-testid={`text-user-email-${user.userId}`}>{user.email}</p>
                        </div>
                        <Badge variant="outline" className="shrink-0" data-testid={`badge-user-role-${user.userId}`}>
                          {user.role}
                        </Badge>
                        <div className="flex items-center gap-2 shrink-0">
                          <div className="flex items-center gap-1">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            {user.gmail.connected ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500" data-testid={`icon-gmail-connected-${user.userId}`} />
                            ) : (
                              <XCircle className="w-4 h-4 text-muted-foreground" data-testid={`icon-gmail-disconnected-${user.userId}`} />
                            )}
                          </div>
                        </div>
                      </div>
                      {user.gmail.connected && user.gmail.email && (
                        <p className="text-xs text-muted-foreground mt-2 ml-0" data-testid={`text-gmail-email-${user.userId}`}>
                          Gmail: {user.gmail.email}
                        </p>
                      )}
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>

            <TabsContent value="workspace" className="flex-1 overflow-auto mt-4">
              <div className="space-y-4">
                <Card data-testid="card-workspace-calendar">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Google Calendar</CardTitle>
                          <CardDescription>
                            Your workspace master calendar — every event, meeting, and talent booking syncs here automatically
                          </CardDescription>
                        </div>
                      </div>
                      {data?.workspaceIntegrations.calendar.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-calendar-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-workspace-calendar-disconnected">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {data?.workspaceIntegrations.calendar.connected ? (
                      <div className="space-y-3">
                        {data.workspaceIntegrations.calendar.isWorkspaceLevel ? (
                          <>
                            <p className="text-sm text-muted-foreground" data-testid="text-workspace-calendar-email">
                              Master calendar connected as: {data.workspaceIntegrations.calendar.email}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Every event, meeting, and talent booking created in this workspace is automatically added to this calendar.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => disconnectCalendarMutation.mutate()}
                              disabled={disconnectCalendarMutation.isPending}
                              data-testid="button-disconnect-calendar"
                            >
                              {disconnectCalendarMutation.isPending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Unlink className="w-4 h-4 mr-2" />
                              )}
                              Disconnect Master Calendar
                            </Button>
                          </>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-sm text-muted-foreground">
                              No dedicated master calendar is connected yet. Connect a Google Calendar to act as this workspace's master calendar — every event, meeting, and talent booking will sync to it automatically.
                            </p>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={handleConnectCalendar}
                              disabled={isConnecting}
                              data-testid="button-connect-own-calendar"
                            >
                              {isConnecting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Link className="w-4 h-4 mr-2" />
                              )}
                              Connect Master Calendar
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Connect a Google Calendar to act as this workspace's master calendar. Every event, meeting, and talent booking created in the workspace is added to it automatically.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleConnectCalendar}
                          disabled={isConnecting}
                          data-testid="button-connect-calendar"
                        >
                          {isConnecting ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Link className="w-4 h-4 mr-2" />
                          )}
                          Connect Master Calendar
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Google Drive workspace card — hidden from UI; queries/mutations remain intact. To re-enable, un-comment this block. */}
                {false && (
                <Card data-testid="card-workspace-drive">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                          <HardDrive className="w-5 h-5 text-yellow-500" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Google Drive</CardTitle>
                          <CardDescription>
                            Read-only Master Schedule sync from a Google Sheet
                          </CardDescription>
                        </div>
                      </div>
                      {data?.workspaceIntegrations.drive.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-drive-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-workspace-drive-disconnected">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {data?.workspaceIntegrations.drive.connected ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground" data-testid="text-workspace-drive-email">
                          Connected as: {data.workspaceIntegrations.drive.email}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disconnectDriveMutation.mutate()}
                          disabled={disconnectDriveMutation.isPending}
                          data-testid="button-disconnect-drive"
                        >
                          {disconnectDriveMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Disconnecting...
                            </>
                          ) : (
                            <>
                              <Unlink className="w-4 h-4 mr-2" />
                              Disconnect Drive
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Connect a Google account to enable read-only Master Schedule sync from a private Google Sheet (paste the sheet's ID or URL). No file browsing — only the sheets you point to are read.
                        </p>
                        <Button
                          size="sm"
                          onClick={() => connectDriveMutation.mutate()}
                          disabled={connectDriveMutation.isPending}
                          data-testid="button-connect-drive"
                        >
                          {connectDriveMutation.isPending ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                          ) : (
                            "Connect Google Drive"
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
                )}

                <Card data-testid="card-workspace-stripe">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-purple-600/10 flex items-center justify-center">
                          <SiStripe className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Stripe</CardTitle>
                          <CardDescription>
                            Accept credit cards and bank transfers
                          </CardDescription>
                        </div>
                      </div>
                      {data?.workspaceIntegrations.stripe?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-stripe-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-workspace-stripe-disconnected">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {data?.workspaceIntegrations.stripe?.connected ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Stripe is connected and ready to accept credit card payments.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowPaymentPortal(true)}
                          data-testid="button-manage-stripe"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Manage Stripe
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Connect your Stripe account to accept credit card payments.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setShowPaymentPortal(true)}
                          data-testid="button-configure-stripe"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Configure Stripe
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="card-workspace-paypal">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-600/10 flex items-center justify-center">
                          <SiPaypal className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">PayPal</CardTitle>
                          <CardDescription>
                            Accept payments via PayPal checkout
                          </CardDescription>
                        </div>
                      </div>
                      {data?.workspaceIntegrations.paypal?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-paypal-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-workspace-paypal-disconnected">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {data?.workspaceIntegrations.paypal?.connected ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          PayPal is connected and ready to accept payments.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => disconnectPayPalMutation.mutate()}
                          disabled={disconnectPayPalMutation.isPending}
                          data-testid="button-disconnect-paypal"
                        >
                          {disconnectPayPalMutation.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Disconnecting...
                            </>
                          ) : (
                            <>
                              <Unlink className="w-4 h-4 mr-2" />
                              Disconnect PayPal
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Configure your PayPal account to accept PayPal payments.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          onClick={() => setShowPaymentPortal(true)}
                          data-testid="button-configure-paypal"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Configure PayPal
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card data-testid="card-workspace-docusign">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-yellow-500/10 flex items-center justify-center">
                          <FileSignature className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">DocuSign</CardTitle>
                          <CardDescription>
                            E-signatures and contract management
                          </CardDescription>
                        </div>
                      </div>
                      {data?.workspaceIntegrations.docusign?.connected ? (
                        <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-docusign-connected">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Connected
                        </Badge>
                      ) : (
                        <Badge variant="secondary" data-testid="badge-workspace-docusign-disconnected">Not Connected</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {data?.workspaceIntegrations.docusign?.connected ? (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground" data-testid="text-workspace-docusign-email">
                          Connected as: {data.workspaceIntegrations.docusign.email}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="button-manage-docusign"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Manage DocuSign
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          Connect DocuSign to send contracts and collect e-signatures from clients.
                        </p>
                        <Button
                          variant="default"
                          size="sm"
                          data-testid="button-connect-docusign"
                        >
                          <Link className="w-4 h-4 mr-2" />
                          Connect DocuSign
                        </Button>
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Webhook signing (HMAC)</p>
                        {data?.workspaceIntegrations.docusignHmac?.isConfigured ? (
                          <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-docusign-hmac-configured">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Configured
                          </Badge>
                        ) : (
                          <Badge variant="secondary" data-testid="badge-docusign-hmac-not-configured">Not Configured</Badge>
                        )}
                      </div>
                      <DocusignHmacCard
                        status={data?.workspaceIntegrations.docusignHmac}
                        workspaceSlug={workspaceSlug}
                        onSaved={() => queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] })}
                      />
                    </div>
                  </CardContent>
                </Card>

                {data?.workspaceIntegrations.eventbrite?.platformEnabled !== false && (
                  <Card data-testid="card-workspace-eventbrite">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-orange-500/10 flex items-center justify-center">
                            <Ticket className="w-5 h-5 text-orange-600" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">Eventbrite</CardTitle>
                            <CardDescription>
                              Ticketed bookings — auto-settle NET payouts and import buyers
                            </CardDescription>
                          </div>
                        </div>
                        {data?.workspaceIntegrations.eventbrite?.connected ? (
                          <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20" data-testid="badge-workspace-eventbrite-connected">
                            <CheckCircle2 className="w-3 h-3 mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Badge variant="secondary" data-testid="badge-workspace-eventbrite-disconnected">Not Connected</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {data?.workspaceIntegrations.eventbrite?.connected ? (
                        <div className="space-y-3">
                          <p className="text-sm text-muted-foreground" data-testid="text-workspace-eventbrite-account">
                            Connected as: {data.workspaceIntegrations.eventbrite.accountName || data.workspaceIntegrations.eventbrite.email}
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            data-testid="button-disconnect-eventbrite"
                            onClick={async () => {
                              try {
                                await apiRequest("DELETE", buildPath("/api/eventbrite/connection"));
                                queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] });
                                toast({ title: "Eventbrite disconnected" });
                              } catch (e: any) {
                                toast({ title: "Failed to disconnect", description: e?.message, variant: "destructive" });
                              }
                            }}
                          >
                            <Unlink className="w-4 h-4 mr-2" />
                            Disconnect Eventbrite
                          </Button>
                        </div>
                      ) : (
                        <EventbriteConnectFlow
                          onConnected={() => queryClient.invalidateQueries({ queryKey: [buildPath("/api/admin/integrations/status")] })}
                        />
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </TabsContent>

            <TabsContent value="talent" className="flex-1 overflow-auto mt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-muted-foreground" data-testid="text-talent-summary">
                  {talentCalendarData?.talent.filter(e => e.calendarConnected).length || 0} of {talentCalendarData?.talent.length || 0} talent have calendar connected
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => refetchTalentCalendars()}
                  disabled={talentCalendarLoading}
                  data-testid="button-refresh-talent"
                >
                  <RefreshCw className={`w-4 h-4 mr-1 ${talentCalendarLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              
              <ScrollArea className="h-[350px]">
                <div className="space-y-3 pr-4">
                  {talentCalendarData?.talent.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Calendar className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No talent found</p>
                    </div>
                  ) : (
                    talentCalendarData?.talent.map((talent) => (
                      <Card key={talent.talentId} className="p-3" data-testid={`card-talent-${talent.talentId}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                              <Calendar className="w-4 h-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{talent.talentName}</p>
                              <p className="text-xs text-muted-foreground">
                                {talent.calendarConnected 
                                  ? talent.calendarEmail 
                                  : talent.talentEmail || 'No email'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {talent.calendarConnected ? (
                              <>
                                <Badge className="bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Connected
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => disconnectTalentCalendarMutation.mutate(talent.talentId)}
                                  disabled={disconnectTalentCalendarMutation.isPending}
                                  data-testid={`button-disconnect-talent-${talent.talentId}`}
                                >
                                  <Unlink className="w-3 h-3 mr-1" />
                                  Disconnect
                                </Button>
                              </>
                            ) : talent.pendingInvite ? (
                              <>
                                <Badge variant="secondary">
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Invite Pending
                                </Badge>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => revokeCalendarInviteMutation.mutate(talent.talentId)}
                                  disabled={revokeCalendarInviteMutation.isPending}
                                  data-testid={`button-revoke-invite-${talent.talentId}`}
                                >
                                  Revoke
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => sendCalendarInviteMutation.mutate(talent.talentId)}
                                  disabled={sendCalendarInviteMutation.isPending}
                                  data-testid={`button-resend-invite-${talent.talentId}`}
                                >
                                  Resend
                                </Button>
                              </>
                            ) : talent.talentEmail ? (
                              <Button
                                size="sm"
                                variant="default"
                                onClick={() => sendCalendarInviteMutation.mutate(talent.talentId)}
                                disabled={sendCalendarInviteMutation.isPending}
                                data-testid={`button-send-invite-${talent.talentId}`}
                              >
                                {sendCalendarInviteMutation.isPending ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Mail className="w-3 h-3 mr-1" />
                                )}
                                Send Invite
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">No email address</span>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
        )}
    </>
  );

  const childDialogs = (
    <>
      <PaymentPortalDialog
        open={showPaymentPortal}
        onOpenChange={(open) => {
          setShowPaymentPortal(open);
          if (!open) {
            refetch();
          }
        }}
      />
    </>
  );

  if (inPanel) {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
          {innerContent}
        </div>
        {childDialogs}
      </>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        {innerContent}
      </DialogContent>
      {childDialogs}
    </Dialog>
  );
}
